import { useState, useCallback, useEffect } from 'react'
import { credentialEvent } from '../config'

export type StorageType = 'local' | 'session'

export const getItem = (key: string): string | null => {
  const valueFromSessionStorage = sessionStorage.getItem(key)

  if (valueFromSessionStorage) {
    try {
      const credentials = JSON.parse(valueFromSessionStorage)

      if (credentials.token) return valueFromSessionStorage
    } catch (e) {}
  }

  return localStorage.getItem(key)
}

const setItem = (
  key: string,
  value: string | null,
  storageType: StorageType
): void => {
  const isInvalid = !Boolean(value) || value === 'null'

  // We are using sessionStorage to storage the JWT when it comes from the URL (it usually happens when
  // the user is impersonated by another person), in this case, the credentials must be removed when the
  // tab is closed and it's only possible when using sessionStorage.
  const storage = storageType === 'local' ? localStorage : sessionStorage

  if (isInvalid) return storage.removeItem(key)

  return storage.setItem(key, value)
}

/* Persist a value in the localStorage or sessionStorage
 *
 * @param any defaultValue - The initial value
 * @param string key - The storage key key
 * @param string storageType - The storage type (localStorage or sessionStorage)
 */
export const useStickyState = <T>(
  defaultValue: T,
  key: string
): [T | null, (value: T | null, storageType: StorageType) => void] => {
  const [value, setValue] = useState(() => {
    const stickyValue = getItem(key)

    return Boolean(stickyValue) ? JSON.parse(stickyValue) : defaultValue
  })

  const handleSetValue = useCallback(
    (value: T | null, storageType: StorageType) => {
      setItem(key, JSON.stringify(value), storageType)
      setValue(value)
    },
    [setItem, key]
  )

  return [value, handleSetValue]
}
